import type { ConsignmentHolding, ConsignmentLineInput } from './api';

/**
 * Row model for Record Sales (Billing).
 *
 * A consignee's holding is ONE quantity per (product, variant), but the same
 * goods may have sold at more than one price — so the dialog lets a holding be
 * split across several rows, each with its own qty and unit price. The rows go
 * to the server exactly as typed (the printed Billing Statement must match the
 * sale); only the stock math is grouped, server-side, per key.
 *
 * All of it is pure so the quantity rules are unit-testable: the component
 * only renders and dispatches. The client clamp is a courtesy — the real guard
 * is the server's guarded `holdingsSubtract`, which 409s
 * `insufficient_consigned_stock` when a concurrent till got there first.
 */
export type BillingRow = {
  rowId: string;
  /** `${productId}|${variantId ?? ''}` — the holding this row draws from. */
  key: string;
  qty: number;
  /** Raw input text, sanitized to digits + one dot; '' until typed. */
  unitPrice: string;
};

export const holdingKey = (h: { productId: string; variantId: string | null }) =>
  `${h.productId}|${h.variantId ?? ''}`;

/** Split a row key back into the ids the API expects. */
export function keyToIds(key: string): { productId: string; variantId: string | null } {
  const [productId = '', variantId = ''] = key.split('|');
  return { productId, variantId: variantId || null };
}

const emptyRow = (key: string, rowId: string): BillingRow => ({
  rowId,
  key,
  qty: 0,
  unitPrice: '',
});

let seq = 0;
/** Row ids are local-only; `crypto.randomUUID` is overkill and not in jsdom. */
export const nextRowId = () => `r${++seq}`;

/** One zero-qty row per holding, in the holdings' own order. */
export function initialRows(holdings: ConsignmentHolding[]): BillingRow[] {
  return holdings.map((h) => emptyRow(holdingKey(h), nextRowId()));
}

/**
 * Re-sync rows against a fresh holdings list WITHOUT losing what was typed.
 *
 * The dialog is rendered inline (`{dialog === 'billing' && <BillingDialog …>}`)
 * and its `detail` prop comes from a query that refetches on window focus, so
 * it re-renders with new data but never remounts. Seeding state once at mount
 * would leave a newly-appearing holding with no row at all — no qty input, no
 * "+" button, and no way to bill it until the dialog is closed and reopened.
 */
export function reconcileRows(rows: BillingRow[], holdings: ConsignmentHolding[]): BillingRow[] {
  const keys = holdings.map(holdingKey);
  const known = new Set(keys);
  // Drop rows for holdings that vanished, but only if nothing was typed into
  // them — a row carrying input stays so the cashier sees what happened.
  const kept = rows.filter((r) => known.has(r.key) || r.qty > 0 || r.unitPrice !== '');
  const present = new Set(kept.map((r) => r.key));
  const added = keys.filter((k) => !present.has(k)).map((k) => emptyRow(k, nextRowId()));
  if (added.length === 0 && kept.length === rows.length) return rows;
  // Keep holdings order, with each key's rows in their existing order.
  const byKey = new Map<string, BillingRow[]>();
  for (const r of [...kept, ...added]) {
    const list = byKey.get(r.key);
    if (list) list.push(r);
    else byKey.set(r.key, [r]);
  }
  const ordered: BillingRow[] = [];
  for (const k of keys) ordered.push(...(byKey.get(k) ?? []));
  for (const [k, list] of byKey) if (!known.has(k)) ordered.push(...list);
  return ordered;
}

export const rowsFor = (rows: BillingRow[], key: string) => rows.filter((r) => r.key === key);

/** Total already claimed for `key` by rows OTHER than `exceptRowId`. */
function claimedByOthers(rows: BillingRow[], key: string, exceptRowId: string): number {
  return rows.reduce((s, r) => (r.key === key && r.rowId !== exceptRowId ? s + r.qty : s), 0);
}

/**
 * The ceiling for ONE row: on-hand minus what its siblings already claim. It
 * deliberately EXCLUDES the row's own qty, so it is the value to hand straight
 * to the input's `max` — adding the row's own qty back would offer numbers the
 * clamp then refuses.
 */
export function remainingFor(
  rows: BillingRow[],
  key: string,
  onHand: number,
  exceptRowId: string,
): number {
  return Math.max(0, onHand - claimedByOthers(rows, key, exceptRowId));
}

export function setRowQty(
  rows: BillingRow[],
  rowId: string,
  wanted: number,
  onHand: number,
): BillingRow[] {
  return rows.map((r) => {
    if (r.rowId !== rowId) return r;
    const ceiling = remainingFor(rows, r.key, onHand, rowId);
    const qty = Math.max(0, Math.min(ceiling, Number.isFinite(wanted) ? Math.floor(wanted) : 0));
    return { ...r, qty };
  });
}

export function setRowPrice(rows: BillingRow[], rowId: string, raw: string): BillingRow[] {
  const clean = raw.replace(/[^0-9.]/g, '');
  return rows.map((r) => (r.rowId === rowId ? { ...r, unitPrice: clean } : r));
}

/** Add another row for the same holding, directly after `afterRowId`. */
export function addSplitRow(rows: BillingRow[], afterRowId: string): BillingRow[] {
  const at = rows.findIndex((r) => r.rowId === afterRowId);
  if (at < 0) return rows;
  const source = rows[at]!;
  const next = [...rows];
  next.splice(at + 1, 0, emptyRow(source.key, nextRowId()));
  return next;
}

/** Remove a split row. A holding's LAST row is never removed — it is the input. */
export function removeRow(rows: BillingRow[], rowId: string): BillingRow[] {
  const row = rows.find((r) => r.rowId === rowId);
  if (!row) return rows;
  if (rowsFor(rows, row.key).length <= 1) return rows;
  return rows.filter((r) => r.rowId !== rowId);
}

/**
 * Holdings whose rows together ask for more than is on hand. Non-empty means
 * the dialog must block submission — this catches the case a per-row clamp
 * cannot: a refetch that LOWERED on-hand under rows that were already valid.
 */
export function overLimit(
  rows: BillingRow[],
  holdings: ConsignmentHolding[],
): Array<{ key: string; onHand: number; total: number }> {
  const out: Array<{ key: string; onHand: number; total: number }> = [];
  for (const h of holdings) {
    const key = holdingKey(h);
    const total = rowsFor(rows, key).reduce((s, r) => s + r.qty, 0);
    if (total > h.qty) out.push({ key, onHand: h.qty, total });
  }
  return out;
}

/** The API payload: every row with a quantity, in display order. */
export function toInvoiceLines(rows: BillingRow[]): ConsignmentLineInput[] {
  return rows
    .filter((r) => r.qty > 0)
    .map((r) => ({ ...keyToIds(r.key), qty: r.qty, unitPrice: r.unitPrice }));
}
